import React, { useEffect } from "react";
import { yupResolver } from "@hookform/resolvers/yup";
import { FormProvider, SubmitHandler, useForm } from "react-hook-form";
import { Button, Dialog, Form, Toast } from "@sscs/ui";
import { ApprovalStatus } from "Types";
import { apiErrorToast } from "Utils";
import yup from "Utils/yup";
import {
  APPROVE_FORM_DEFAULT_VALUES,
  APPROVE_REASONS,
  APPROVE_STATUS_OPTIONS,
  REJECT_REASONS,
} from "./constants";
import Reason from "./Reason";
import type { ApprovalDialogFormData, DialogProps } from "./types";

const FormSchema = yup.object({
  status: yup.mixed<ApprovalStatus>().required(),
  reason: yup
    .string()
    .required("Please specify reason for approval change")
    .max(500, "Max reason length is 500 characters"),
});

const ApprovalDialogContent = ({
  onClose,
  subjectId,
  editApproval,
}: Omit<DialogProps, "isOpen">): React.JSX.Element => {
  const formMethods = useForm<ApprovalDialogFormData>({
    resolver: yupResolver(FormSchema),
    defaultValues: APPROVE_FORM_DEFAULT_VALUES,
  });

  const watchStatus = formMethods.watch("status");
  const formSetValue = formMethods.setValue;

  useEffect(() => {
    formSetValue("reason", "");
  }, [watchStatus, formSetValue]);

  const onSubmit: SubmitHandler<ApprovalDialogFormData> = async (data) => {
    try {
      await editApproval({
        subjectId,
        body: {
          approval_status: data.status,
          reason: data.reason,
        },
      }).unwrap();
    } catch (error) {
      apiErrorToast(error, "Could not save approval changes");
      return;
    }

    Toast.show({
      title: "Approval updated",
      type: "success",
    });

    onClose();
  };

  const predefinedReasons =
    watchStatus === "ApprovalRejected" ? REJECT_REASONS : APPROVE_REASONS;

  return (
    <FormProvider {...formMethods}>
      <Form.Form onSubmit={formMethods.handleSubmit(onSubmit)}>
        <Dialog.Header>
          <Dialog.Header.Title>Change approval status</Dialog.Header.Title>
        </Dialog.Header>
        <Dialog.Body>
          <Dialog.Section>
            <Dialog.Section.Content>
              <Form.RadioGroup
                id="approve-status"
                name="status"
                orientation="horizontal"
                items={APPROVE_STATUS_OPTIONS}
              />
              <Reason selection={predefinedReasons} />
            </Dialog.Section.Content>
          </Dialog.Section>
        </Dialog.Body>
        <Dialog.Footer>
          <Dialog.Footer.Actions>
            <Button color="secondary" onClick={onClose}>
              Cancel
            </Button>
            <Button
              type="submit"
              color="primary"
              disabled={formMethods.formState.isSubmitting}
            >
              Confirm
            </Button>
          </Dialog.Footer.Actions>
        </Dialog.Footer>
      </Form.Form>
    </FormProvider>
  );
};

const ApprovalDialog = ({
  isOpen,
  ...props
}: DialogProps): React.JSX.Element => (
  <Dialog.Frame isOpen={isOpen} onClose={props.onClose}>
    <ApprovalDialogContent {...props} />
  </Dialog.Frame>
);

export default ApprovalDialog;
